// @flow

import {
  Route,
  Debug,
  Environment,
  type DebugContext,
  type EnvironmentContext,
} from '@ta/lithium.context';
import { requestTTICallback } from '@ta/platform.tti';
import * as React from 'react';

/**
 * "Key/value pairs which generate breakdowns charts and search filters"
 * https://docs.sentry.io/enriching-error-data/context/?platform=browser#tagging-events
 */
export type SentryTags = {|
  /** The logger name, i.e. the source of where we're filing the error from. Examples: ErrorBoundary, @ta/common.logging, non-components. */
  logger: string,
|};

/**
 * "Arbitrary unstructured data which is stored with an event sample"
 * https://docs.sentry.io/enriching-error-data/context/?platform=browser#extra-context
 */
export type SentryExtraData = {|
  /** Whether the user was logged in during this event. */
  isLoggedInMember?: boolean,
|};

/** Information for filing an error to Sentry */
export type SentryError = {|
  error: Error,
  tags: SentryTags,
  extraData?: SentryExtraData,
|};

/** Name of the project within the Sentry app that the event should be sent to. */
export type SentryProject = 'tripadvisor-web-app' | 'lithium-web-app';

export const sendSentryRequest = (Sentry: any, sentryError: SentryError) => {
  // withScope isolates the extra info to just this Sentry call. Global data set in sentry.jsx will remain intact.
  Sentry.withScope(scope => {
    Object.entries(sentryError.tags).forEach(([key, value]) => {
      scope.setTag(key, value);
    });

    if (sentryError.extraData) {
      Object.entries(sentryError.extraData).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(sentryError.error);
  });
};

export const LOGGER_COMMON_LOGGING = 'commonLogging';

let sentryInitialized = false;
let sentryEventAllowance = __CONFIG__.max_events_per_min; // Start at the max number of events per minute, decrement per event and reset on a timer.
let sentryEventTimer = null;

/**
 * Sentry is enormous.  The JS modules are over 20k gzipped.  To reduce the suffering, we dynamically import it in this
 * function, with the expectation that we're called as a low priority function (i.e. requestIdleCallback).
 *
 * @return the imported Sentry module, which must be provided back to other functions in this module so that we can
 * log to sentry.
 */
export const installSentry = (
  { serverName, drsInfo }: DebugContext,
  pageName: string,
  { version, domainName }: EnvironmentContext,
  project: SentryProject = 'tripadvisor-web-app',
): Promise<any> =>
  Promise.all([import('@sentry/browser'), import('@sentry/integrations')]).then(
    ([Sentry, Integrations]) => {
      // A lot of the configuration is one-time only . . .
      if (!sentryInitialized) {
        sentryInitialized = true;

        const sentryDsn = `https://sentry@${domainName}/SentryProxy`;

        let environment;
        if (serverName === 'www.tripadvisor.com' || serverName === 'www.tripadvisor.cn') {
          environment = 'live';
        } else if (serverName === 'hare.tripadvisor.com') {
          environment = 'hare';
        } else if (serverName === 'webml.dev.tripadvisor.com') {
          environment = 'webml';
        } else {
          environment = 'dev';
        }

        const sentryConfig = {
          dsn: sentryDsn,
          release: version,
          environment,
          // something is broken with the 'App Only' tab in sentry. To avoid showing the tab at all, use some garbage here so
          // it doesn't match anything as 'In App' and so doesn't display the tab. See:
          // https://forum.sentry.io/t/problem-with-includepaths-app-only/4404
          includePaths: ['rutabaga_cyclone'],
          integrations: [new Integrations.CaptureConsole({ levels: ['error'] })],
          beforeSend: event => {
            // As a measure against overloading Sentry ourselves, we have a maximum number of errors we will send per minute.
            // We don't wait until the very end in case we miss consecutive errors (one which could be a hint to debugging another), nor do we really want a setInterval constantly running. Instead, start timer halfway through maximum event allowance.
            if (
              sentryEventAllowance === Math.ceil(__CONFIG__.max_events_per_min / 2) &&
              sentryEventTimer === null
            ) {
              sentryEventTimer = setTimeout(() => {
                sentryEventAllowance = __CONFIG__.max_events_per_min;
                sentryEventTimer = null;
              }, 60000);
            }
            if (sentryEventAllowance === 0) {
              // We've reached the max number of errors until the timer resets next.
              return null; // Returning null will avoid sending this event to the server.
            }
            sentryEventAllowance--;

            // We don't want Sentry to come back and hit ThreatMatrix (t1.tacdn.com) again with the session_id because it leads to issues. So unfortunately, we must strip it out before recording it.
            // See RB description for more info: FRAUD-245
            if (event && event.exception && event.exception.values) {
              event.exception.values.forEach(val => {
                if (val && val.stacktrace && val.stacktrace.frames) {
                  val.stacktrace.frames.forEach(frame => {
                    if (frame && frame.filename && frame.filename.includes('t1.tacdn.com')) {
                      // eslint-disable-next-line no-param-reassign
                      frame.filename = frame.filename.replace(
                        /session_id(.*)(?=&)/,
                        'session_id=STRIPPED_BY_SENTRY',
                      );
                    }
                  });
                }
              });
            }
            return event;
          },
        };

        Sentry.init(sentryConfig);
        Sentry.configureScope(scope => {
          scope.setTag('hostName', domainName);
          scope.setTag('runMode', DEBUG ? 'mainline' : 'production');
          scope.setTag('serverName', serverName);
          scope.setTag('logger', 'jsDefault');
          scope.setExtra('drsInfo', drsInfo);
          scope.setExtra('sentryProject', project);
        });

        // We polyfill console.error to capture errors before we load so that we don't lose them in sentry.
        if (typeof __PRE_SENTRY_ERRORS__ !== 'undefined' && __PRE_SENTRY_ERRORS__) {
          __PRE_SENTRY_ERRORS__.forEach(errorArgs => {
            sendSentryRequest(Sentry, {
              error:
                errorArgs.length === 1 && typeof errorArgs[0] === 'object'
                  ? errorArgs[0]
                  : new Error(errorArgs.join(' ')),
              tags: { logger: LOGGER_COMMON_LOGGING },
            });
          });
        }
        // Communicate back to the polyfill to stop buffering errors.
        // eslint-disable-next-line no-global-assign
        __PRE_SENTRY_ERRORS__ = null;
      }

      // . . . but a few things will change over time.
      Sentry.configureScope(scope => {
        scope.setTag('page', pageName);
      });

      return Sentry;
    },
  );

type State =
  | {| +status: 'uninitialized' |}
  | {| +status: 'initializing', +done: Promise<any> |}
  | {| +status: 'initialized' |};

type Props = {
  +installer?: (DebugContext, string, EnvironmentContext, SentryProject) => Promise<any>,
  +fallbackPageName?: string,
  +project: SentryProject,
};

/**
 * A wrapper around the sentry installer that defers installation, because Sentry So Big.
 */
export const useSentry = ({
  installer: propsInstaller,
  fallbackPageName,
  project = 'tripadvisor-web-app',
}: Props = {}) => {
  const installer = propsInstaller || installSentry;

  const debug = React.useContext(Debug);
  const [route] = React.useContext(Route);
  const environment = React.useContext(Environment);
  const state = React.useRef<State>({ status: 'uninitialized' });

  React.useEffect(() => {
    // This is complicated.  We don't want to install sentry until we've hit a requestIdleCallback, but we may
    // execute this effect additional times before that happens.  We want to enqueue those changes in that case so that
    // every effect is serialized.
    //
    // Only the first call to installSentry is actually asynchronous; subsequent ones are synchronous configuration
    // updates.

    if (state.current.status === 'uninitialized') {
      // For initial load of sentry, wait until we're idle to do the big load (the JS is enormous).
      state.current = {
        status: 'initializing',
        done: new Promise(resolve => {
          requestTTICallback(() =>
            requestIdleCallback(() => {
              installer(debug, route.page || fallbackPageName, environment, project).then(() => {
                resolve();
                state.current = {
                  status: 'initialized',
                };
              });
            }),
          );
        }),
      };
    } else if (state.current.status === 'initializing') {
      state.current = {
        status: 'initializing',
        done: state.current.done.then(() => installer(debug, route.page, environment, project)),
      };
    } else {
      installer(debug, route.page, environment, project);
    }
  }, [debug, route, environment, installer, fallbackPageName, project]);
};

export const Sentry = (props: Props) => {
  useSentry(props);
  return null;
};
